import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api, plural, type LabelPreview, type LabelTemplate } from '../api.ts';
import { Icon } from '../components/Icon.tsx';
import { RollLabelPreview, downloadRollLabel, type RollRender } from '../components/RollLabel.tsx';

/**
 * Печать этикеток (ТЗ 6.2, 6.3, макет 6).
 *
 * Два разных устройства и, значит, два разных выхода:
 *
 *   — ЛИСТ: сетка наклеек на A4 для обычного принтера, PDF в миллиметрах.
 *     Так печатают пачкой, раз в полгода.
 *   — ЛЕНТА: карманный термопринтер-этикеточник. Он печатает не документ,
 *     а картинку, которую ему отдаёт своё приложение, поэтому здесь
 *     готовится PNG ровно в разрешении головки. Так печатают по одной
 *     штуке — ровно тогда, когда камень лёг в коробку.
 *
 * Предпросмотр в обоих случаях рисуется в миллиметрах: этикетка — будущий
 * физический объект, и увидеть её надо такой, какой она выйдет.
 */
export function LabelsScreen() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const only = params.get('ids');

  const [templates, setTemplates] = useState<LabelTemplate[]>([]);
  const [templateKey, setTemplateKey] = useState('a4-65');
  const [labels, setLabels] = useState<LabelPreview[] | null>(null);
  const [chosen, setChosen] = useState<Set<string> | null>(null);
  const [previewId, setPreviewId] = useState<string | null>(null);
  const [skip, setSkip] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [render, setRender] = useState<RollRender | null>(null);

  useEffect(() => {
    void api.labelTemplates().then((r) => setTemplates(r.templates));
    void api.labelPreview(only ?? undefined).then((r) => {
      setLabels(r.labels);
      setChosen(new Set(r.labels.map((l) => l.id)));
      setPreviewId(r.labels[0]?.id ?? null);
    });
  }, [only]);

  const template = templates.find((t) => t.key === templateKey);
  const roll = template?.kind === 'roll';
  const sheets = templates.filter((t) => t.kind === 'sheet');
  const rolls = templates.filter((t) => t.kind === 'roll');

  const perPage = template ? template.columns * template.rows : 0;
  const selected = useMemo(
    () => (labels ?? []).filter((l) => chosen?.has(l.id)),
    [labels, chosen],
  );
  const pages = perPage ? Math.ceil((skip + selected.length) / perPage) : 0;
  const preview = (labels ?? []).find((l) => l.id === previewId) ?? selected[0] ?? labels?.[0];

  function toggle(id: string) {
    setChosen((current) => {
      const next = new Set(current ?? []);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function downloadPdf() {
    setBusy(true);
    setError(null);
    try {
      await api.labelPdf({
        ids: selected.map((l) => l.id),
        template: templateKey,
        skip: roll ? 0 : skip,
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось собрать PDF');
    } finally {
      setBusy(false);
    }
  }

  /**
   * Картинки для приложения принтера. По одному файлу на этикетку и с
   * паузой между ними: браузер на телефоне сохраняет их по очереди и на
   * пачку без задержки отвечает «слишком много загрузок».
   */
  async function downloadImages(items: LabelPreview[]) {
    if (!template) return;
    setBusy(true);
    setError(null);
    try {
      for (const item of items) {
        await downloadRollLabel(item, template);
        if (items.length > 1) await new Promise((resolve) => setTimeout(resolve, 400));
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось сохранить картинку');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="app">
      <div className="topbar topbar--bordered">
        <button className="icon-btn" onClick={() => navigate(-1)} aria-label="Назад">
          <Icon name="back" className="icon muted" />
        </button>
        <div className="spacer" />
        <h2 className="topbar-title">Печать этикеток</h2>
        <div className="spacer" />
        <span style={{ width: 20 }} />
      </div>

      <div className="scroll">
        <div className="label-key" style={{ marginTop: 'var(--space-4)' }}>
          Чем печатать
        </div>
        <div className="segment">
          <button
            className={!roll ? 'is-selected' : ''}
            onClick={() => setTemplateKey(sheets[0]?.key ?? 'a4-65')}
          >
            Лист A4
          </button>
          <button
            className={roll ? 'is-selected' : ''}
            onClick={() => setTemplateKey(rolls[0]?.key ?? 'roll-15x30')}
          >
            Лента, термопринтер
          </button>
        </div>

        <div className="label-key">Предпросмотр — реальный размер</div>

        {preview && template ? (
          roll ? (
            <RollLabelPreview label={preview} template={template} onRendered={setRender} />
          ) : (
            <div
              className="label"
              style={{
                width: `${template.label.width}mm`,
                height: `${template.label.height}mm`,
              }}
            >
              <img
                className="qr"
                style={{ width: `${template.qr}mm`, height: `${template.qr}mm`, background: 'none' }}
                src={api.qrUrl(preview.id)}
                alt=""
              />
              <div className="lines">
                <b>{preview.title}</b>
                {preview.place && <span>{preview.place}</span>}
                <code>{preview.footer}</code>
              </div>
            </div>
          )
        ) : (
          <div className="muted small">Загрузка…</div>
        )}

        {template && (
          <div className="hint">
            {template.label.height} × {template.label.width} мм
            {roll
              ? ` · печать ${template.printWidth} мм · ${template.dpi ?? 203} dpi`
              : ` · QR ${template.qr} мм`}
            {roll && render
              ? ` · код ${render.qrSideMm.toFixed(1)} мм, модуль ${render.moduleMm.toFixed(2)} мм`
              : !roll
                ? `, модуль ${(template.qr / 25).toFixed(2)} мм с полем в 4 модуля`
                : ''}
          </div>
        )}

        <div className="label-key">{roll ? 'Лента' : 'Лист'}</div>
        <div className="segment segment--wrap">
          {(roll ? rolls : sheets).map((t) => (
            <button
              key={t.key}
              className={t.key === templateKey ? 'is-selected' : ''}
              onClick={() => setTemplateKey(t.key)}
            >
              {t.title}
            </button>
          ))}
        </div>
        {template && <div className="hint">{template.hint}</div>}

        {roll ? (
          <>
            <dl className="rows">
              <div className="row">
                <dt>Образцов выбрано</dt>
                <dd>
                  {selected.length} из {labels?.length ?? 0}
                </dd>
              </div>
              <div className="row">
                <dt>Картинка</dt>
                <dd>
                  {render ? `${render.widthPx} × ${render.heightPx} точек` : '—'}
                  <div className="hint">ровно в разрешении головки, без масштабирования</div>
                </dd>
              </div>
            </dl>

            <div className="notice notice--warn" style={{ marginTop: 'var(--space-4)' }}>
              <Icon name="alert" className="icon icon-sm" />
              <span>
                В приложении принтера вставляйте картинку «как есть», во всю этикетку,
                и не включайте подгонку или сглаживание — от них код перестаёт читаться.
              </span>
            </div>

            {error && (
              <div className="notice notice--danger" style={{ marginTop: 'var(--space-3)' }}>
                {error}
              </div>
            )}

            <div className="actions" style={{ marginTop: 'var(--space-4)' }}>
              <button
                className="btn btn--primary"
                disabled={busy || !preview}
                onClick={() => preview && void downloadImages([preview])}
              >
                <Icon name="download" className="icon icon-sm" /> PNG этикетки
              </button>
              {selected.length > 1 && (
                <button
                  className="btn"
                  disabled={busy}
                  onClick={() => void downloadImages(selected)}
                >
                  Все {selected.length} картинок
                </button>
              )}
            </div>

            <button
              className="btn btn--full"
              style={{ marginTop: 'var(--space-2)' }}
              disabled={busy || selected.length === 0}
              onClick={downloadPdf}
            >
              <Icon name="printer" className="icon icon-sm" /> PDF: страница на этикетку
            </button>
            <div className="hint centered">
              Если принтер виден системе как обычный — печатайте PDF без полей
            </div>

            <div className="label-key">Как печатать с телефона</div>
            <ol className="steps">
              <li>Сохраните PNG кнопкой выше — он ляжет в загрузки или галерею.</li>
              <li>Откройте приложение принтера и создайте этикетку нужного размера.</li>
              <li>Вставьте картинку из галереи и растяните на всю этикетку.</li>
              <li>Печать. Проверьте штатной камерой, что код открывает образец.</li>
            </ol>
          </>
        ) : (
          <>
            <dl className="rows">
              <div className="row">
                <dt>Образцов выбрано</dt>
                <dd>
                  {selected.length} из {labels?.length ?? 0}
                </dd>
              </div>
              <div className="row">
                <dt>Сетка листа</dt>
                <dd>
                  {template ? `${template.columns} × ${template.rows} · A4` : '—'}
                  {pages > 1 && (
                    <div className="hint">
                      {pages} {plural(pages, 'лист', 'листа', 'листов')}
                    </div>
                  )}
                </dd>
              </div>
              <div className="row">
                <dt>Пропустить этикеток</dt>
                <dd>
                  <input
                    className="field-input field-compact"
                    inputMode="numeric"
                    style={{ width: '5rem', textAlign: 'right' }}
                    value={skip}
                    onChange={(e) =>
                      setSkip(
                        Math.min(Number(e.target.value.replace(/\D/g, '') || 0), perPage - 1 || 0),
                      )
                    }
                  />
                </dd>
              </div>
            </dl>
            {skip > 0 && (
              <div className="hint">
                Первые {skip} мест на листе останутся пустыми — для листа, с которого уже отклеили
              </div>
            )}

            <div className="notice notice--warn" style={{ marginTop: 'var(--space-4)' }}>
              <Icon name="alert" className="icon icon-sm" />
              <span>
                Печатайте без масштабирования — «фактический размер», а не «по размеру
                страницы». Иначе код уменьшится и перестанет читаться.
              </span>
            </div>

            {error && (
              <div className="notice notice--danger" style={{ marginTop: 'var(--space-3)' }}>
                {error}
              </div>
            )}

            <div className="actions" style={{ marginTop: 'var(--space-4)' }}>
              <button
                className="btn"
                onClick={() => setChosen(new Set((labels ?? []).map((l) => l.id)))}
                disabled={selected.length === (labels?.length ?? 0)}
              >
                Весь лист
              </button>
              <button
                className="btn btn--primary"
                onClick={downloadPdf}
                disabled={busy || selected.length === 0}
              >
                <Icon name="download" className="icon icon-sm" /> {busy ? 'Собираем…' : 'PDF'}
              </button>
            </div>
          </>
        )}

        <div className="label-key">Что печатать</div>
        <div className="pick-list">
          {(labels ?? []).map((item) => (
            <div key={item.id} className="pick-row-wrap">
              <button
                type="button"
                className={chosen?.has(item.id) ? 'pick-row is-on' : 'pick-row'}
                aria-pressed={chosen?.has(item.id)}
                onClick={() => toggle(item.id)}
              >
                <span className={chosen?.has(item.id) ? 'tick is-on' : 'tick'} aria-hidden="true">
                  <Icon name="check" className="icon icon-xs" />
                </span>
                <span className="pick-text">
                  {item.title}
                  <span className="hint">{item.footer}</span>
                </span>
              </button>
              {/* На ленте печатают по одной штуке, поэтому важно видеть
                  именно ту этикетку, которую собираешься печатать. */}
              {roll && (
                <button
                  type="button"
                  className={item.id === previewId ? 'chip' : 'chip chip--plain'}
                  onClick={() => setPreviewId(item.id)}
                >
                  показать
                </button>
              )}
            </div>
          ))}
        </div>

        <div className="hint" style={{ marginBottom: 'var(--space-6)' }}>
          Номер печатается текстом всегда: когда домен перестанет работать, QR станет
          узором, а строка с номером останется полноценной этикеткой.
        </div>
      </div>
    </div>
  );
}
